import styled from 'styled-components';
import { DownOutlined } from '@ant-design/icons';
import { Collapse, Form, Select, Space, Table, Typography } from 'antd';

const { Paragraph } = Typography;

const GREY_50 = '#F8F8F8';
const GREY_100 = '#F0F0F0';
const GREY_500 = '#262626';
const GREY_250 = '#BFBFBF';
const WHITE_COLOR = '#ffffff';
const WHITE_BLUE_COLOR = '#EBEEFD';

const BODY_LARGE_REGULAR = `
	font-size: 18px;
	font-style: normal;
	font-weight: 400;
	line-height: 24px;
`;

const LABEL_SMALL_REGULAR = `
	font-size: 14px;
	font-style: normal;
	font-weight: 400;
	line-height: 16px;
	letter-spacing: -0.07px;
`;

const LABEL_SMALL_SEMIBOLD = `
	font-size: 14px;
	font-style: normal;
	font-weight: 600;
	line-height: 16px;
`;

export const LABEL_MEDIUM_REGULAR = `
	font-size: 16px;
	font-style: normal;
	font-weight: 400;
	line-height: 24px;
	letter-spacing: -0.08px;
	color: ${GREY_500};
`;

export const LABEL_MEDIUM_REGULAR_PLACEHOLDER = `
	font-size: 16px;
	font-style: normal;
	font-weight: 400;
	line-height: 24px;
	letter-spacing: -0.08px;
	color: ${GREY_250};
`;

export const LABEL_MEDIUM_SEMIBOLD = `
	font-size: 16px;
	font-style: normal;
	font-weight: 600;
	line-height: 24px;
`;

const HEADING_EXTRASMALL_SEMIBOLD = `
	font-size: 16px;
	font-style: normal;
	font-weight: 600;
	line-height: 24px;
	letter-spacing: -0.24px;
`;

const LABEL_LARGE_SEMIBOLD = `
	font-size: 18px;
	font-style: normal;
	font-weight: 600;
	line-height: 24px;
	letter-spacing: -0.09px;
	color: ${GREY_500};
`;

const LABEL_LARGE_REGULAR = `
	font-size: 18px;
	font-style: normal;
	font-weight: 400;
	line-height: 24px;
	letter-spacing: -0.09px;
	color: ${GREY_500};
`;

const HEADING_MEDIUM_SEMIBOLD = `
	font-size: 25px;
	font-style: normal;
	font-weight: 600;
	line-height: 32px;
	letter-spacing: -0.125px;
`;

export const DownOutlinedIcon = styled(DownOutlined)<{ open?: boolean }>`
	display: inline-block;
	transform: ${({ open }) => (open ? 'rotate(180deg)' : 'rotate(0)')};
	transition: all 0.3s ease;
`;

export const StyledSpace = styled(Space)<{ $isOpen: boolean }>`
	.anticon {
		display: inline-block;
		transform: ${({ $isOpen }) => ($isOpen ? 'rotate(180deg)' : 'rotate(0)')};
		transition: transform 0.3s ease;
	}
`;

export const CustomSelect = styled(Select)`
	.ant-select-selector {
		border-radius: 4px !important;
	}
`;

export const CollapseStyled = styled(Collapse)`
	border-radius: 8px;

	.ant-collapse-item:last-child {
		border-radius: 8px;
	}

	.ant-collapse-item:last-child {
		.ant-collapse-content {
			border-radius: 0 0 8px 8px;
		}
	}
`;

export const HeadingMediumSemibold = styled(Typography.Text)`
	${HEADING_MEDIUM_SEMIBOLD}
	color: ${GREY_500};
`;

export const LabelMediumSemibold = styled(Typography.Text)`
	${LABEL_MEDIUM_SEMIBOLD}
	color: ${GREY_500};
`;

export const BodyLargeRegular = styled(Typography.Text)`
	${BODY_LARGE_REGULAR}
	color: ${GREY_500};
`;

export const LabelSmallRegular = styled(Typography.Text)`
	${LABEL_SMALL_REGULAR}
	color: ${GREY_500};
`;

export const HeadingExtraSmallSemibold = styled(Typography.Text)`
	${HEADING_EXTRASMALL_SEMIBOLD}
	color: ${GREY_500};
`;

export const LabelSmallSemibold = styled(Typography.Text)`
	${LABEL_SMALL_SEMIBOLD}
	color: ${GREY_500};
`;

export const LabelMediumRegular = styled(Typography.Text)`
	${LABEL_MEDIUM_REGULAR}
`;

export const LabelMediumRegularPlaceholder = styled(Typography.Text)`
	${LABEL_MEDIUM_REGULAR_PLACEHOLDER}
`;

export const LabelLargeSemibold = styled(Typography.Text)`
	${LABEL_LARGE_SEMIBOLD}
`;

export const LabelLargeRegular = styled(Typography.Text)`
	${LABEL_LARGE_REGULAR}
`;

export const LabelSmallRegularLink = styled(Typography.Link)`
	${LABEL_SMALL_REGULAR}
`;

export const FormItemWrapper = styled(Form.Item)`
	margin-bottom: 0;
`;

export const InlineParagraph = styled(Paragraph)`
	margin-bottom: 0;
	display: inline;
`;

export const TooltipWrapper = styled.span`
	margin-left: 8px;
	display: inline-block;
`;

export const CustomFormItem = styled(Form.Item)`
	margin-bottom: 0 !important;

	.ant-form-item-label label,
	.ant-form-item-label > label {
		${LABEL_MEDIUM_REGULAR}
		height: 100% !important;
	}
`;

export const CustomTable: typeof Table = styled(Table)`
	overflow: hidden;
	border-radius: 0.5rem;

	.ant-table-thead {
		tr {
			border-bottom: 2px solid ${GREY_50};
		}

		.ant-table-cell.ant-table-selection-column {
			padding: 0 1rem;
		}

		.ant-table-cell {
			font-weight: 600;
			padding: 0.8rem 0.4rem;
			background-color: ${GREY_100};

			&:not(:last-child):not(:first-child) {
				border-right: 2px solid ${WHITE_COLOR};
			}

			&::before {
				display: none;
			}
		}
	}

	.ant-table-tbody {
		.ant-table-cell {
			background-color: ${GREY_50};
		}

		td {
			padding: 1rem;
			border-bottom: 2px solid ${WHITE_COLOR};
		}

		tr:last-child > td {
			border-bottom: none;
		}

		.ant-table-row-selected {
			background-color: ${WHITE_BLUE_COLOR};

			.ant-table-cell {
				background-color: transparent;
			}
		}
	}
`;
